/**
 * Sentry error tracking service
 * Handles error capture, breadcrumbs, and session replay
 * Falls back to console logging if Sentry DSN not configured
 */

interface SentryContext {
  [key: string]: any;
}

interface Breadcrumb {
  category: string;
  message: string;
  level: 'info' | 'warning' | 'error';
  data?: SentryContext;
}

// Try to import @sentry/react if available, otherwise use fallback
let SentrySDK: any = null;
try {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  SentrySDK = require('@sentry/react');
} catch {
  // Sentry not installed, using fallback
}

const Sentry = {
  /**
   * Initialize Sentry with DSN from environment
   * Call once in main.tsx before React render
   */
  init: function () {
    // `process.env` n'existe pas côté navigateur : Vite y émet un objet VIDE. Cette garde était
    // donc toujours vraie et `Sentry.init` n'a JAMAIS été appelé en production — aucune remontée
    // d'erreur, silencieusement. Seul `import.meta.env` est alimenté au build.
    if (!SentrySDK || !import.meta.env.REACT_APP_SENTRY_DSN) {
      console.log('[Sentry] Skipped init: no DSN or SDK unavailable');
      return;
    }

    try {
      const BrowserTracing = SentrySDK.BrowserTracing;
      const Replay = SentrySDK.Replay;

      SentrySDK.init({
        dsn: import.meta.env.REACT_APP_SENTRY_DSN,
        environment: process.env.NODE_ENV || 'development',
        // Sample perf traces low in prod (cost + data minimisation, RGPD art. 32);
        // full sampling in dev. Tunable via env without a redeploy.
        tracesSampleRate: Number(
          import.meta.env.REACT_APP_SENTRY_TRACES_RATE ??
            (process.env.NODE_ENV === 'production' ? 0.1 : 1.0),
        ),
        integrations: [
          new BrowserTracing(),
          new Replay({
            maskAllText: true,
            blockAllMedia: true,
          }),
        ],
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
      });
      console.log('[Sentry] Initialized successfully');
    } catch (e) {
      console.error('[Sentry] Init failed:', e);
    }
  },

  addBreadcrumb(breadcrumb: Breadcrumb) {
    if (SentrySDK) {
      SentrySDK.addBreadcrumb({
        message: breadcrumb.message,
        category: breadcrumb.category,
        level: breadcrumb.level,
        data: breadcrumb.data,
      });
    }
    console.log(`[Sentry breadcrumb:${breadcrumb.category}]`, breadcrumb.message, breadcrumb.data);
  },

  captureMessage(
    message: string,
    level: 'info' | 'warning' | 'error' = 'info',
    context?: SentryContext
  ) {
    if (SentrySDK) {
      SentrySDK.captureMessage(message, level);
      if (context) {
        SentrySDK.setContext('custom', context);
      }
    }
    console.log(`[Sentry ${level}]`, message, context);
  },

  captureException(error: Error | unknown, context?: { contexts?: SentryContext }) {
    if (SentrySDK) {
      SentrySDK.captureException(error);
      if (context?.contexts) {
        SentrySDK.setContext('writer_bot', context.contexts);
      }
    }
    console.error('[Sentry Exception]', error, context);
  },

  /**
   * Wrap a function call to automatically capture errors
   */
  captureAsync<T>(
    fn: () => Promise<T>,
    operation: string,
    fallback?: T
  ): Promise<T> {
    return Promise.resolve()
      .then(() => fn())
      .catch(error => {
        Sentry.captureException(error, { contexts: { operation } });
        if (fallback !== undefined) return fallback as T;
        throw error;
      });
  },
};

export default Sentry;
